/**
 * Product packaging: a product is bought in packages (a 100 ml bottle, a carton of 12)
 * but priced, stocked and sold per base unit (ml, stk).
 *
 * The base unit is the unit of record. `inPrice`, `price`, sale line `qty` and `inStock`
 * are always per base unit. Selling a package is a quantity shortcut on the sale line:
 * qty = packages * packageSize. Nothing downstream (stock, reports, exports) changes.
 *
 * Spec: docs/superpowers/specs/2026-09-30-product-packaging-design.md
 */

const round = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

export const toNumber = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const num = parseFloat(String(val).replace(',', '.'));
    return Number.isNaN(num) ? null : num;
};

export const hasPackaging = (product) => {
    const size = toNumber(product?.packageSize);
    return size !== null && size > 1;
};

export const deriveInPrice = ({ packageInPrice, packageSize } = {}) => {
    const price = toNumber(packageInPrice);
    const size = toNumber(packageSize);
    if (price === null || size === null || size <= 0) return null;
    return round(price / size, 2);
};

export const derivePrice = ({ inPrice, desiredMargin } = {}) => {
    const cost = toNumber(inPrice);
    const margin = toNumber(desiredMargin);
    if (cost === null || margin === null) return null;
    return Math.round(cost * (1 + margin / 100));
};

export const packagesToBaseQty = (packages, packageSize) => {
    const count = toNumber(packages) || 0;
    const size = toNumber(packageSize);
    if (size === null || size <= 0) return count;
    return round(count * size, 4);
};

export const baseQtyToPackages = (qty, packageSize) => {
    const base = toNumber(qty) || 0;
    const size = toNumber(packageSize);
    if (size === null || size <= 0) return base;
    return round(base / size, 4);
};

export const baseUnitName = (product) => {
    const name = (product?.baseUnit || '').trim();
    return name || 'stk';
};

export const packageUnitName = (product) => {
    const name = (product?.packageUnit || '').trim();
    return name || 'pakning';
};

export const packageCaption = (qty, product) => `= ${toNumber(qty) || 0} ${baseUnitName(product)}`;

/**
 * fields.js callback shared by packageInPrice and packageSize. Derives inPrice, then price
 * when a desired margin is set. updateValue is FormFieldsCompact's updateValue: it writes the
 * store and, when id is set, saves the field.
 */
export const packagingCallback = (obj = {}, updateValue = () => {}) => {
    const inPrice = deriveInPrice(obj);
    if (inPrice === null) return;
    updateValue({ id: obj.id, name: 'inPrice', value: inPrice });
    const price = derivePrice({ inPrice, desiredMargin: obj.desiredMargin });
    if (price === null) return;
    updateValue({ id: obj.id, name: 'price', value: price });
};

export const isPackagingEnabled = (customer) => parseInt(customer?.featureFlags?.productPackaging, 10) === 1;
